import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCRMAuth } from '../../context/CRMAuthContext';
import axios from 'axios';
import { 
  Users, Target, DollarSign, TrendingUp, Activity, 
  ArrowUpRight, ArrowDownRight, Calendar, Clock,
  BarChart3, PieChart, UserPlus, FileText
} from 'lucide-react';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const CRMDashboard = () => {
  const { token, company } = useCRMAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/crm/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      </CRMLayout>
    );
  }

  const stats = dashboardData?.stats || {};

  const statCards = [
    { 
      label: 'Total Leads', 
      value: stats.total_leads || 0, 
      icon: Target, 
      color: 'blue',
      change: '+12%',
      positive: true 
    },
    { 
      label: 'Active Deals', 
      value: stats.active_deals || 0, 
      icon: BarChart3, 
      color: 'purple',
      change: '+8%',
      positive: true 
    },
    { 
      label: 'Won Deals', 
      value: stats.won_deals || 0, 
      icon: TrendingUp, 
      color: 'green',
      change: '+23%',
      positive: true 
    },
    { 
      label: 'Total Revenue', 
      value: `$${(stats.total_revenue || 0).toLocaleString()}`, 
      icon: DollarSign, 
      color: 'orange',
      change: '+15%',
      positive: true 
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      green: 'bg-green-500/10 text-green-400 border-green-500/20',
      orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    };
    return colors[color] || colors.blue;
  };

  return (
    <CRMLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-slate-400">Welcome back! Here's your CRM overview.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/crm/leads/new"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Add Lead
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <div
              key={index}
              className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-5"
            >
              <div className="flex items-start justify-between">
                <div className={`p-2 rounded-lg border ${getColorClasses(stat.color)}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div className={`flex items-center gap-1 text-sm ${stat.positive ? 'text-green-400' : 'text-red-400'}`}>
                  {stat.positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {stat.change}
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pipeline Overview & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline Summary */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Pipeline Overview</h2>
              <Link to="/crm/deals" className="text-sm text-blue-400 hover:text-blue-300">
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {dashboardData?.deals_by_stage?.length > 0 ? (
                dashboardData.deals_by_stage.map((stage, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        stage._id === 'closed_won' ? 'bg-green-400' :
                        stage._id === 'closed_lost' ? 'bg-red-400' :
                        'bg-blue-400'
                      }`}></div>
                      <span className="text-slate-300 capitalize">{stage._id?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-medium">{stage.count} deals</span>
                      <span className="text-slate-400 text-sm ml-2">${stage.value?.toLocaleString()}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <PieChart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No deals yet</p>
                  <Link to="/crm/deals/new" className="text-blue-400 text-sm hover:underline">
                    Create your first deal
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
              <Link to="/crm/activities" className="text-sm text-blue-400 hover:text-blue-300">
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {dashboardData?.recent_activities?.length > 0 ? (
                dashboardData.recent_activities.slice(0, 5).map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Activity className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-300 truncate">{activity.description}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No recent activity</p>
                  <p className="text-sm">Activity will appear here as you use the CRM</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-white">{stats.total_contacts || 0}</p>
            <p className="text-sm text-slate-400">Total Contacts</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-white">{stats.lost_deals || 0}</p>
            <p className="text-sm text-slate-400">Lost Deals</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-400">${(stats.pipeline_value || 0).toLocaleString()}</p>
            <p className="text-sm text-slate-400">Pipeline Value</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-white">{stats.total_users || 1}</p>
            <p className="text-sm text-slate-400">Team Members</p>
          </div>
        </div>
      </div>
    </CRMLayout>
  );
};

export default CRMDashboard;
